import { wrap, type Remote } from 'comlink';
import type { VirtualBackgroundWorkerType } from '@src/workers/virtual-background.worker';
// Dynamic import usage for worker-loader to prevent Jest syntax errors

/**
 * Virtual Background Service
 *
 * **Singleton service** for managing the virtual background web worker lifecycle.
 * Ensures only one worker instance exists across the entire application.
 *
 * **Key Features:**
 * - Single worker instance (singleton pattern)
 * - Reference counting for proper cleanup
 * - Auto-termination after idle period (30s)
 * - Prevents memory leaks from orphaned workers
 *
 * **Architecture:**
 * - Uses Comlink for worker communication
 * - Lazy initialization (worker created on first use)
 * - Automatic cleanup when no longer needed
 *
 * @example
 * // Get worker API (creates or reuses existing worker)
 * const api = await virtualBackgroundService.getWorkerApi();
 *
 * // When done, release reference
 * virtualBackgroundService.releaseWorker();
 */
class VirtualBackgroundService {
  private worker: Worker | null = null;
  private workerApi: Remote<VirtualBackgroundWorkerType> | null = null;
  private refCount = 0;
  private initializationPromise: Promise<Remote<VirtualBackgroundWorkerType>> | null = null;

  // MEMORY LEAK FIX: Auto-terminate worker after idle period
  private idleTimeoutId: NodeJS.Timeout | null = null;
  private readonly IDLE_TIMEOUT_MS = 30000; // 30 seconds

  /**
   * Get Worker API
   *
   * Returns the singleton worker API instance, creating it if necessary.
   * Increments reference count and cancels any pending termination.
   *
   * **Usage Pattern:**
   * ```typescript
   * const api = await virtualBackgroundService.getWorkerApi();
   * // Use api...
   * // When done:
   * virtualBackgroundService.releaseWorker();
   * ```
   *
   * @returns {Promise<Remote<VirtualBackgroundWorkerType>>} Worker API proxy
   * @throws {Error} If worker initialization fails
   */
  public async getWorkerApi(): Promise<Remote<VirtualBackgroundWorkerType>> {
    // Clear idle timeout if someone requests the worker
    if (this.idleTimeoutId !== null) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    this.refCount++;

    if (this.workerApi) {
      return this.workerApi;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      try {
        // Dynamically import loader to avoid Parse Error in Jest (due to import.meta in worker-loader)
        const { createVirtualBackgroundWorker } = await import('@src/workers/worker-loader');

        this.worker = createVirtualBackgroundWorker();
        this.workerApi = wrap<VirtualBackgroundWorkerType>(this.worker);

        return this.workerApi;
      } catch (error) {
        console.error('[VB Service] Failed to initialize worker:', error);
        // If init fails, NOBODY got the worker. Reset count to 0.
        // Decrementing (--) only accounts for the creator, not shared waiters.
        this.refCount = 0;
        this.initializationPromise = null;
        throw error;
      }
    })();

    return this.initializationPromise;
  }

  /**
   * Get Active Worker (Peek)
   * Returns the worker ONLY if it is already active or initializing.
   * Does NOT increment reference count.
   * Useful for configuration updates that should only happen if the worker is already running.
   */
  public getActiveWorker(): Remote<VirtualBackgroundWorkerType> | Promise<Remote<VirtualBackgroundWorkerType>> | null {
    if (this.workerApi) {
      return this.workerApi;
    }
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    return null;
  }

  /**
   * Release Worker Reference
   *
   * Decrements the reference count. When count reaches zero, schedules
   * automatic termination after IDLE_TIMEOUT_MS (30 seconds).
   *
   * **Why Delayed Termination:**
   * - Allows quick re-use if user toggles effects on/off
   * - Prevents expensive worker re-initialization
   * - Cleans up memory if genuinely unused
   *
   * **Call this when:**
   * - Component unmounts
   * - Effect is removed
   * - No longer processing backgrounds
   *
   * @example
   * useEffect(() => {
   *   return () => {
   *     virtualBackgroundService.releaseWorker();
   *   };
   * }, []);
   */
  public releaseWorker() {
    this.refCount = Math.max(0, this.refCount - 1);

    // MEMORY LEAK FIX: Auto-terminate after idle period when no consumers
    if (this.refCount === 0 && this.workerApi) {
      // Clear any existing timeout
      if (this.idleTimeoutId !== null) {
        clearTimeout(this.idleTimeoutId);
      }

      this.idleTimeoutId = setTimeout(() => {
        // Double-check refCount is still 0
        if (this.refCount === 0) {
          this.terminate();
        }
        this.idleTimeoutId = null;
      }, this.IDLE_TIMEOUT_MS);
    }
  }

  /**
   * Terminate Worker
   *
   * Immediately terminates the worker and resets all state.
   * Cancels any pending termination timers.
   *
   * **Use sparingly** - Usually not needed as auto-termination handles cleanup.
   *
   * **When to call:**
   * - Application shutdown
   * - Forced cleanup (rare)
   * - Testing scenarios
   *
   * @example
   * // On app unmount
   * virtualBackgroundService.terminate();
   */
  public async terminate() {
    // Clear any pending idle timeout
    if (this.idleTimeoutId !== null) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    // Clean up worker resources before terminating
    // This ensures ML models (MediaPipe/ONNX) are properly disposed
    if (this.workerApi) {
      try {
        await this.workerApi.cleanup();
      } catch (error) {
        // Worker may already be dead, ignore errors
        console.warn('[VB Service] Cleanup failed:', error);
      }
    }

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerApi = null;
    this.initializationPromise = null;
    this.refCount = 0;
  }

  /**
   * Synchronous termination for page unload events.
   * Skips graceful cleanup and immediately kills the worker thread.
   */
  public terminateImmediately() {
    if (this.idleTimeoutId !== null) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerApi = null;
    this.initializationPromise = null;
    this.refCount = 0;
  }
}

export const virtualBackgroundService = new VirtualBackgroundService();

// Ensure worker is terminated when the page is unloaded/refreshed
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    virtualBackgroundService.terminateImmediately();
  });
}
